import { createSlice, createAction } from '@reduxjs/toolkit';
import { JSONEditorSlice, StoreVM } from '../view-models';
import CONSTANTS from '../constants/constants';
import jsonConstants from '../constants/jsonConstants';
import defaultEnvs from '../constants/defaultEnvs';
import { cloneDeep } from 'lodash';

let att: any = {};
CONSTANTS.AAD_ATTRIBUTES.forEach((a) => (att[a] = { id: a, dataType: 'string' }));

export const initialState: JSONEditorSlice = {
  nodeData: {},
  jsonSchemaVersion: jsonConstants.editorJsonSchemaVersion,
  flowcharts: {},
  currentFlowchartId: '',
  currentOpenNodeId: '',
  currentTabId: '',
  claims: {},
  aadAttributes: {},
  ssoSession: {
    persistedClaims: [
      {
        claimId: 'objectId',
        defaultValue: '',
      }
    ],
    outputClaims: [],
    components: [],
    sessionFlags: {},
  },
  templatization: {
    templateFields: Object.keys(jsonConstants.defaultAppSettingFields).reduce((tfs: any, defField) => { tfs[defField] = { id: defField, name: defField, nodeFields: {} }; return tfs; }, {}),
    environments: Object.keys(jsonConstants.defaultEnvMap).reduce((envs: {[envId: string]: any}, defaultEnvKey) => { if (jsonConstants[defaultEnvKey as keyof typeof jsonConstants]) { envs[jsonConstants.defaultEnvMap[defaultEnvKey as keyof typeof jsonConstants.defaultEnvMap]] = cloneDeep(defaultEnvs[jsonConstants.defaultEnvMap[defaultEnvKey as keyof typeof jsonConstants.defaultEnvMap] as keyof typeof defaultEnvs]) }; return envs; }, {}),
    templatedComponents: {}
  },
  validationResults: {},
};

const jsonEditorSlice = createSlice({
  name: 'jsonEditor',
  initialState,
  reducers: {
    setNodeData: (state, { payload }) => {
      const key = Object.keys(payload)[0];
      const data = payload[key];
      state.nodeData = payload ? { ...state.nodeData, [key]: data } : { ...state.nodeData };
    },
    setFlowcharts: (state, { payload }) => {
      if (payload.hasOwnProperty('currentFlowchartId')) {
        state.currentFlowchartId = payload['currentFlowchartId'];
        state.currentTabId = payload['currentFlowchartId'];
        state.claims = { ...state.claims, ...payload['claims'] };
        state.aadAttributes = { ...state.aadAttributes, ...payload['aadAttributes'] };
      }
      state.flowcharts[state.currentTabId] = payload.hasOwnProperty('flowchart') ? payload['flowchart'] : {};
    },
    setCurrentFlowchartName: (state, { payload }) => {
      state.currentFlowchartId = payload ? payload : '';
    },
    setCurrentTabId: (state, { payload }) => {
      state.currentTabId = payload ? payload : '';
    },
    setCurrentOpenNodeId: (state, { payload }) => {
      state.currentOpenNodeId = payload ? payload : '';
    },
    deleteSubFlowById: (state, { payload }) => {
      const _allFC = cloneDeep(state.flowcharts);
      const parentCompId = _allFC[payload['id']]['parentComponentId'];
      const _components = _allFC[payload['id']]['components'];

      const _allNodeData = cloneDeep(state.nodeData);
      _components.forEach((comp: string) => {
        delete _allNodeData[comp];
      });

      state.nodeData = cloneDeep(_allNodeData);
      state.nodeData[parentCompId][payload['subFlowType']] = { displayName: '', id: '' };
      delete _allFC[payload['id']];
      state.flowcharts = cloneDeep(_allFC);
    },
    updateNodeLabel: (state, { payload }) => {
      const _nodes = cloneDeep(state.flowcharts[state.currentTabId]['nodes']);
      const newNodes = _nodes.reduce((acc: any[], curVal: any) => {
        if (curVal['id'] === payload['id']) {
          return acc.concat({ ...curVal, data: { label: payload['value'] } });
        } else return acc.concat(curVal);
      }, []);
      state.flowcharts[state.currentTabId]['nodes'] = newNodes;
    },
    deleteNodeById: (state, { payload }) => {
      let _nodeData = { ...state.nodeData };
      delete _nodeData[payload];
      state.nodeData = _nodeData;
      const _nodes = cloneDeep([...state.flowcharts[state.currentTabId]['nodes']])
        .filter((node: any) => node.id !== payload)
        .filter((node: any) => node['parentNodeId'] !== payload);
      const _remNodesList = _nodes.reduce((acc: string[], curNode: any) => acc.concat(curNode.id), []);
      state.flowcharts[state.currentTabId] = {
        ...state.flowcharts[state.currentTabId],
        components: [...state.flowcharts[state.currentTabId]['components']].filter((comp: string) => comp !== payload),
        nodes: _nodes,
        edges: [...state.flowcharts[state.currentTabId]['edges']]
          .filter((edge: any) => _remNodesList.includes(edge.source))
          .filter((edge: any) => _remNodesList.includes(edge.target)),
      };
    },
    deleteEdgeById: (state, { payload }) => {
      state.flowcharts[state.currentTabId] = {
        ...state.flowcharts[state.currentTabId],
        edges: [...state.flowcharts[state.currentTabId]['edges']].filter((edge: any) => edge.id !== payload),
      };
    },
    setCurrentOpenNodeData: (state, { payload }) => {
      state.nodeData[state.currentOpenNodeId] = payload ? payload : '';
    },
    setSubFC: (state, { payload }) => {
      state.nodeData[state.currentOpenNodeId][payload['subFlowType']] = {
        displayName: payload['flowchartName'],
        id: payload['id'],
      };
      state.flowcharts[payload['id']] = payload;
    },
    // add claims to the first extension file
    setJSONClaims: (state, { payload }) => {
      state.claims[payload['id']] = payload;
    },
    setJSONAADAttributes: (state, { payload }) => {
      state.aadAttributes[payload['id']] = payload;
    },
    deleteJSONSessionFlagById: (state, { payload }) => {
      state.ssoSession = {
        ...state.ssoSession,
        sessionFlags: Object.keys(state.ssoSession.sessionFlags).reduce((res: { [index: string]: any }, curKey: string) => {
          if (curKey !== payload.id) {
            res[curKey] = state.ssoSession.sessionFlags[curKey];
          }
          return res;
        }, {})
      }
    },
    setJSONSessionFlag: (state, { payload }) => {
      state.ssoSession.sessionFlags = {
        ...state.ssoSession.sessionFlags,
        [payload['id']]: payload
      }
    },
    setJSONSSOSession: (state, { payload }) => {
      state.ssoSession = {
        ...state.ssoSession,
        persistedClaims: [ ...payload.persistedClaims ],
        outputClaims: [ ...payload.outputClaims ],
        components: [ ...payload.components ]
      }
    },
    setJSONEditor: (state, { payload }) => {
      return { ...payload }
    },
    setValidationResults: (state, { payload }) => {
      state.validationResults = payload || {};
    },
    setTemplatizationField: (state, { payload }) => {
      const key = Object.keys(payload)[0];
      const fieldData = payload[key];
      state.templatization.templateFields = payload ? { ...state.templatization.templateFields, [key]: fieldData } : { ...state.templatization.templateFields };
    },
    deleteTemplateFieldById: (state, { payload }) => {
      state.templatization = {
        ...state.templatization,
        templateFields: Object.keys(state.templatization.templateFields).reduce((tFields: { [index: string]: any }, curField) => { if (curField !== payload) { tFields[curField] = state.templatization.templateFields[curField]; }; return tFields; }, {}),
        environments: Object.keys(state.templatization.environments).reduce((envs: { [index: string]: any }, curEnv) => {
          envs[curEnv] = { ...state.templatization.environments[curEnv], settings: Object.keys(state.templatization.environments[curEnv].settings).reduce((sts: { [index: string]: any }, curSt) => { if (curSt !== payload) { sts[curSt] = state.templatization.environments[curEnv].settings[curSt]; }; return sts; }, {}) };
          return envs;
        }, {})
      }
    },
    setTemplatizationEnvironment: (state, { payload }) => {
      const key = Object.keys(payload)[0];
      const envData = payload[key];
      state.templatization.environments = payload ? { ...state.templatization.environments, [key]: envData } : { ...state.templatization.environments };
    },
    deleteTemplatizationEnvironment: (state, { payload }) => {
      let _envs = { ...state.templatization.environments };
      delete _envs[payload];
      state.templatization.environments = _envs;
    },
    setTemplatedComponent: (state, { payload }) => {
      const key = Object.keys(payload)[0];
      state.templatization.templatedComponents = payload ? { ...state.templatization.templatedComponents, [key]: payload[key] } : { ...state.templatization.templatedComponents };
    },
    deleteTemplatedComponent: (state, { payload }) => {
      let _tcs = { ...state.templatization.templatedComponents };
      delete _tcs[payload];
      state.templatization.templatedComponents = _tcs;
    }
  },
});

export const getJsonEditor = (state: StoreVM) => state.jsonEditor;
export const getFlowchartNodesEdges = (state: StoreVM) =>
  state.jsonEditor.currentTabId !== ''
    ? {
        nodes: state.jsonEditor.flowcharts[state.jsonEditor.currentTabId].nodes,
        edges: state.jsonEditor.flowcharts[state.jsonEditor.currentTabId].edges,
      }
    : {
        nodes: [],
        edges: [],
      };

export const getNodeData = (state: StoreVM) => state.jsonEditor.nodeData;
export const getCurrentFlowchartId = (state: StoreVM) => state.jsonEditor.currentFlowchartId;
export const getCurrentOpenNodeId = (state: StoreVM) => state.jsonEditor.currentOpenNodeId;
export const getCurrentTabId = (state: StoreVM) => state.jsonEditor.currentTabId;
export const getCurrentTabData = (state: StoreVM) => state.jsonEditor.flowcharts[state.jsonEditor.currentTabId];
export const getFlowcharts = (state: StoreVM) => state.jsonEditor.flowcharts;
export const getJSONClaims = (state: StoreVM) => state.jsonEditor.claims;
export const getJSONAADAttributes = (state: StoreVM) => state.jsonEditor.aadAttributes;
export const getJSONSession = (state: StoreVM) => state.jsonEditor.ssoSession;
export const getValidationResults = (state: StoreVM) => state.jsonEditor.validationResults;
export const getNodeDataById = (id: string) => (state: StoreVM) => state.jsonEditor.nodeData[id];
export const getNodeDataTypeById = (id: string) => (state: StoreVM) =>
  state.jsonEditor.nodeData.hasOwnProperty(id) ? state.jsonEditor.nodeData[id]['componentType'] : '';
export const getTemplatizationFields = (state: StoreVM) => state.jsonEditor.templatization.templateFields;
export const getTemplatizationEnvironments = (state: StoreVM) => state.jsonEditor.templatization.environments;
export const getTemplatedComponents = (state: StoreVM) => state.jsonEditor.templatization.templatedComponents;

export const {
  setNodeData,
  setFlowcharts,
  setCurrentFlowchartName,
  setCurrentTabId,
  updateNodeLabel,
  setJSONClaims,
  setSubFC,
  setCurrentOpenNodeId,
  deleteNodeById,
  deleteEdgeById,
  setCurrentOpenNodeData,
  setJSONAADAttributes,
  setJSONSessionFlag,
  setJSONSSOSession,
  deleteJSONSessionFlagById,
  setJSONEditor,
  setValidationResults,
  deleteSubFlowById,
  setTemplatizationEnvironment,
  setTemplatizationField,
  deleteTemplateFieldById,
  deleteTemplatizationEnvironment,
  setTemplatedComponent,
  deleteTemplatedComponent
} = jsonEditorSlice.actions;

export const { reducer: jsonEditorReducer } = jsonEditorSlice;
